/*
 * Trainer left rail (formerly inlined inside TrainerDashboard.tsx).
 *
 * Encapsulates the trainer-side primary navigation: brand strip with collapse / pin,
 * the nav items (Members / Library / Chat / Settings), and the bottom
 * user-avatar menu (Sign Out).
 *
 * Used by:
 *   - TrainerDashboard.tsx (dashboard chrome)
 *   - TrainerWorkoutDetailPage.tsx (re-uses the same nav rather than drawing its own rail)
 *
 * State ownership:
 *   - `activeSection` is OWNED by the parent (each surface has different navigation
 *     semantics: dashboard mutates internal state; the detail page navigates back to
 *     `/trainer-dashboard` with a localStorage hint). The rail just reflects whatever
 *     the parent passes and emits clicks via `onSectionSelect`.
 *   - All UI-local state (collapse, pin, hover-expand, label fade, avatar menu open)
 *     is managed inside this component and persisted to localStorage so it carries
 *     across pages naturally — same keys (`trainer_sidebar_collapsed`,
 *     `trainer_sidebar_pinned`) the dashboard always used.
 *
 * Visual / behavioral parity:
 *   This component is a 1:1 lift-and-shift of the previous in-dashboard sidebar.
 *   AC #24 of `trainer-workout-detail-page-design-update-dev-handoff.md` requires that
 *   the dashboard sidebar remain pixel-identical post-refactor.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronDown,
  Library as LibraryIcon,
  LogOut,
  MessageCircle,
  Pin,
  Settings,
  Users,
} from '../bravosIcons';
import { BravosWordmark } from '../../../components/BravosWordmark';
import { useAuth } from '../../../contexts/AuthContext';
import { CHAT_NAV_ENABLED } from '../../../lib/chatNavEnabled';
import { accountNavDisplayName, getInitials } from '../../../pages/account/accountShared';

export type TrainerSection = 'members' | 'library' | 'chat' | 'control';

interface MenuItemDef {
  id: TrainerSection;
  label: string;
  icon: typeof Users;
}

const ALL_MENU_ITEMS: MenuItemDef[] = [
  { id: 'members', label: 'Members', icon: Users },
  { id: 'library', label: 'Library', icon: LibraryIcon },
  { id: 'chat', label: 'Chat', icon: MessageCircle },
  { id: 'control', label: 'Settings', icon: Settings },
];

const MENU_ITEMS: MenuItemDef[] = CHAT_NAV_ENABLED
  ? ALL_MENU_ITEMS
  : ALL_MENU_ITEMS.filter((item) => item.id !== 'chat');

interface Props {
  /** Which menu item should render in the active state. */
  activeSection: TrainerSection;
  /** Called when a menu item is clicked. Parent decides whether to mutate local state
   *  (dashboard) or navigate elsewhere (workout detail page). */
  onSectionSelect: (id: TrainerSection) => void;
}

export const TrainerLeftRail: React.FC<Props> = ({ activeSection, onSectionSelect }) => {
  const navigate = useNavigate();
  const { user, profile, signOut } = useAuth();

  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(() => {
    try {
      const v = localStorage.getItem('trainer_sidebar_collapsed');
      if (v === 'true') return true;
      if (v === 'false') return false;
    } catch {
      // ignore
    }
    return true;
  });

  const [isPinned, setIsPinned] = useState<boolean>(() => {
    try {
      const v = localStorage.getItem('trainer_sidebar_pinned');
      if (v === 'true') return true;
      if (v === 'false') return false;
    } catch {
      // ignore
    }
    return false;
  });

  const [showLongTitle, setShowLongTitle] = useState<boolean>(() => {
    try {
      const v = localStorage.getItem('trainer_sidebar_collapsed');
      if (v === 'false') return true;
    } catch {
      // ignore
    }
    return false;
  });

  const [showPinIcon, setShowPinIcon] = useState<boolean>(() => {
    try {
      const pinned = localStorage.getItem('trainer_sidebar_pinned');
      const collapsed = localStorage.getItem('trainer_sidebar_collapsed');
      if (pinned === 'true') return true;
      if (collapsed === 'false') return true;
    } catch {
      // ignore
    }
    return false;
  });

  const [isUserMenuOpen, setIsUserMenuOpen] = useState<boolean>(false);

  /** Persist pinned + collapsed across pages — same keys the dashboard always used. */
  useEffect(() => {
    try {
      localStorage.setItem('trainer_sidebar_pinned', String(isPinned));
    } catch {
      // ignore
    }
  }, [isPinned]);

  useEffect(() => {
    try {
      localStorage.setItem('trainer_sidebar_collapsed', String(sidebarCollapsed));
    } catch {
      // ignore
    }
  }, [sidebarCollapsed]);

  /** Lets fixed UI (e.g. workout edit save bar) inset from the live rail width when expanded. */
  useEffect(() => {
    document.documentElement.style.setProperty(
      '--trainer-rail-width',
      sidebarCollapsed ? '68px' : '20rem',
    );
    return () => {
      document.documentElement.style.removeProperty('--trainer-rail-width');
    };
  }, [sidebarCollapsed]);

  const handleSignOut = async () => {
    setIsUserMenuOpen(false);
    await signOut();
    navigate('/auth');
  };

  const showBravosFullWordmark = !sidebarCollapsed && showLongTitle;
  const showBravosIconMark = !showBravosFullWordmark;

  return (
    <div
      className={`hidden lg:flex lg:flex-col shrink-0 bg-card border-r border-line transition-all duration-300 relative z-10 ${
        sidebarCollapsed ? 'w-[68px]' : 'w-80'
      }`}
      onMouseEnter={() => {
        if (sidebarCollapsed && !isPinned) {
          setSidebarCollapsed(false);
          /** Show long title at 85% of expansion (300ms * 0.85 = 255ms) */
          setTimeout(() => {
            setShowLongTitle(true);
          }, 255);
          /** Show pin icon at 100% of expansion (300ms) */
          setTimeout(() => {
            setShowPinIcon(true);
          }, 300);
        }
      }}
      onMouseLeave={() => {
        if (!isPinned) {
          setShowLongTitle(false);
          setShowPinIcon(false);
          setSidebarCollapsed(true);
        }
      }}
    >
      {/* Brand row: icon centered in collapsed rail (68px) via fixed-width strip at sidebar x=0.
           Wordmark left edge matches that icon (h-9, aspect 583/660). */}
      <div className="relative border-b border-line flex-shrink-0 select-none pt-4 pb-4 pl-[14px] pr-4">
        <div className="relative h-12 w-full min-w-0">
          {/* Icon ↔ wordmark crossfade (300ms, matches sidebar width transition) */}
          <div
            className={`absolute -left-[14px] top-0 z-0 flex h-12 w-[68px] items-center justify-center transition-opacity duration-300 ease-in-out ${
              showBravosIconMark ? 'opacity-100' : 'opacity-0 pointer-events-none'
            }`}
          >
            <BravosWordmark
              variant="icon"
              className="h-9 w-auto shrink-0"
            />
          </div>
          <div
            className={`absolute left-[calc((68px-2.25rem*583/660)/2-14px)] top-0 right-10 z-0 flex h-12 min-w-0 items-center transition-opacity duration-300 ease-in-out whitespace-nowrap ${
              showBravosFullWordmark ? 'opacity-100' : 'opacity-0 pointer-events-none'
            }`}
          >
            <BravosWordmark className="h-9 w-auto max-w-full shrink-0" />
          </div>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (sidebarCollapsed) return;
              const newPinnedState = !isPinned;
              setIsPinned(newPinnedState);
              if (newPinnedState) {
                setShowLongTitle(true);
                setShowPinIcon(true);
              }
            }}
            className={`absolute right-0 top-1/2 z-10 -translate-y-1/2 p-1 rounded transition-all duration-300 ${
              isPinned
                ? 'bg-subtle text-theme-text'
                : 'hover:bg-subtle text-secondary'
            } ${sidebarCollapsed ? 'pointer-events-none opacity-0' : ''} ${
              showPinIcon || isPinned ? 'opacity-100' : 'opacity-0 pointer-events-none'
            }`}
            aria-label={isPinned ? 'Unpin sidebar' : 'Pin sidebar'}
            title={isPinned ? 'Unpin the sidebar' : 'Pin the sidebar'}
            tabIndex={sidebarCollapsed ? -1 : 0}
          >
            <Pin className={`w-5 h-5 ${isPinned ? 'fill-current' : ''}`} />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-hidden">
        <nav className="space-y-0">
          {MENU_ITEMS.map((item) => {
            const Icon = item.icon;
            const isActive = activeSection === item.id;

            /** v1.1 visual treatment:
             *  - Collapsed (any state): the box hugs the icon — small square.
             *  - Expanded: the box ALWAYS extends to the row's right edge, regardless of
             *    active / hover. This means: when active it's a green pill across icon +
             *    label; when hovered it's a cream pill across icon + label; when neither
             *    it's a transparent extended box (visually nothing). The bg classes below
             *    decide what shows; the geometry is the same so the hover and active
             *    highlights look consistent. */
            const iconBoxExtended = showLongTitle;

            const iconBoxClass = isActive
              ? 'bg-action-secondary'
              : 'group-hover:bg-subtle';

            const iconColorClass = isActive
              ? 'text-inverse'
              : 'text-secondary group-hover:text-theme-text';

            const textColorClass = isActive
              ? 'text-inverse font-semibold'
              : 'text-theme-text font-medium';

            return (
              <button
                key={item.id}
                type="button"
                onClick={() => onSectionSelect(item.id)}
                className="group w-full flex items-center relative text-left px-4 py-6"
                title={sidebarCollapsed ? item.label : undefined}
              >
                <div
                  className={`absolute left-[14px] p-2 rounded-lg flex-shrink-0 transition-colors ${iconBoxClass}`}
                  style={iconBoxExtended ? { right: '1rem' } : undefined}
                >
                  <Icon className={`w-5 h-5 transition-colors ${iconColorClass}`} />
                </div>
                <div
                  className={`absolute transition-opacity duration-200 ${
                    showLongTitle ? 'opacity-100' : 'opacity-0 pointer-events-none'
                  }`}
                  style={{
                    left: '4.5rem',
                    right: '1rem',
                  }}
                >
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm whitespace-nowrap relative ${textColorClass}`}>
                      {item.label}
                    </p>
                  </div>
                </div>
              </button>
            );
          })}
        </nav>
      </div>

      {/* User menu at bottom */}
      <div className="border-t border-line bg-card flex-shrink-0">
        <div className="relative">
          <button
            type="button"
            onClick={() => setIsUserMenuOpen((v) => !v)}
            className="w-full flex items-center text-theme-text dark:text-theme-off-white hover:text-accent-violet dark:hover:text-accent-lime transition-colors relative"
            style={{ paddingTop: '2.5rem', paddingBottom: '2.5rem', paddingLeft: '1rem', paddingRight: '1rem' }}
            title={sidebarCollapsed ? accountNavDisplayName(profile, user) : undefined}
          >
            <div
              className="absolute left-[14px] flex-shrink-0 flex items-center justify-center rounded-full bg-black dark:bg-white"
              style={{
                width: '2.25rem',
                height: '2.25rem',
                minWidth: '2.25rem',
                minHeight: '2.25rem',
              }}
            >
              <span className="text-xs font-semibold text-white dark:text-black leading-none">
                {getInitials(accountNavDisplayName(profile, user))}
              </span>
            </div>
            <div
              className={`absolute transition-opacity duration-200 ${
                showLongTitle ? 'opacity-100' : 'opacity-0 pointer-events-none'
              }`}
              style={{
                left: '4.5rem',
                right: '2.5rem',
              }}
            >
              <span className="text-sm font-medium whitespace-nowrap overflow-hidden text-ellipsis block text-left">
                {accountNavDisplayName(profile, user)}
              </span>
            </div>
            <div
              className={`absolute transition-opacity duration-200 ${
                showLongTitle ? 'opacity-100' : 'opacity-0 pointer-events-none'
              }`}
              style={{
                right: '1rem',
              }}
            >
              <ChevronDown className="w-4 h-4 flex-shrink-0" />
            </div>
          </button>

          {isUserMenuOpen && (
            <div
              className={`absolute ${sidebarCollapsed ? 'bottom-full left-0 mb-2' : 'bottom-full left-0 mb-2'} w-full bg-card rounded-md shadow-lg py-1 z-50 border border-line`}
            >
              <button
                type="button"
                onClick={handleSignOut}
                className="block w-full text-left px-4 py-2 text-sm text-theme-text dark:text-theme-off-white hover:bg-theme-muted dark:hover:bg-white/10"
              >
                <LogOut className="w-4 h-4 inline mr-2" />
                Sign Out
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TrainerLeftRail;
